import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, deleteDoc, doc, updateDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { useHouseholdSubscription } from "@/hooks/useHouseholdSubscription";
import { usePaywall } from "@/contexts/PaywallContext";
import { isFeatureUnlocked } from "@/lib/subscription";
import { ZoeyPlusLockBadge } from "@/components/features/LockedFeatureOverlay";
import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { GroceryCategories } from "@/lib/constants";
import { fromTimestamp } from "@/lib/firestore";
import { relativeDate } from "@/lib/humanDate";
import { SendToStoreSheet } from "@/components/features/SendToStoreSheet";
import { useTranslations } from "@/contexts/I18nContext";
import type { GroceryListItem, MemberProfile } from "@/lib/models";

interface GroceryPrice {
  uid?: string;
  cheapest_price: number;
  store?: string;
  source?: string;
}

export function GroceryPage() {
  const navigate = useNavigate();
  const t = useTranslations();
  const { householdId, household, members, profile, loading: ctxLoading } = useHouseholdContext();
  // Affiliate-link CTAs (Send to Store) are adult-only per the Amazon
  // Associates Operating Agreement and family-app rules. See
  // [[activate-amazon-associates-tag-zoeyorganizer-20-ac]].
  const isAdult = profile?.memberType === "adult" || profile?.memberType === "elderly_parent";

  const { data: items, loading: itemsLoading } = useFirestoreCollection<GroceryListItem>(
    householdId ? `households/${householdId}/groceryListItems` : null
  );

  const { data: groceryPrices } = useFirestoreCollection<GroceryPrice>(
    householdId ? `households/${householdId}/grocery_prices` : null
  );

  // Build a price lookup map by normalized name
  const priceMap = useMemo(() => {
    const map: Record<string, GroceryPrice> = {};
    for (const price of groceryPrices) {
      if (price.uid) {
        map[price.uid.toLowerCase().trim()] = price;
      }
    }
    return map;
  }, [groceryPrices]);

  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState<string>(GroceryCategories[0]);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  // Tag-cloud filter — when set, the list scopes to the selected
  // category (mirrors iOS GroceryListView TagCloudView quick-filter
  // pattern at `Views/Groceries/GroceryListView.swift:142`).
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string | null>(null);
  const [showRecentlyPurchased, setShowRecentlyPurchased] = useState(false);
  const [showBuyAgain, setShowBuyAgain] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanBanner, setScanBanner] = useState<{ count: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { state: subState } = useHouseholdSubscription();
  const { openPaywall } = usePaywall();
  const receiptScanLocked = !isFeatureUnlocked("receipt_scanning", subState);

  const loading = ctxLoading || itemsLoading;
  const [sendStoreQuery, setSendStoreQuery] = useState<string | null>(null);

  const memberMap = useMemo(() => {
    const map: Record<string, MemberProfile> = {};
    for (const m of members) {
      if (m.uid) map[m.uid] = m;
    }
    return map;
  }, [members]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, GroceryListItem[]> = {};
    for (const item of items) {
      const cat = item.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    // Sort within each group: uncompleted first, then by created_at desc
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        const aTime = a.created_at?.seconds ?? 0;
        const bTime = b.created_at?.seconds ?? 0;
        return bTime - aTime;
      });
    }
    return groups;
  }, [items]);

  // Sort categories by GroceryCategories order
  const sortedCategories = useMemo(() => {
    const cats = Object.keys(groupedItems);
    return cats.sort((a, b) => {
      const aIdx = (GroceryCategories as readonly string[]).indexOf(a);
      const bIdx = (GroceryCategories as readonly string[]).indexOf(b);
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });
  }, [groupedItems]);

  // Recently purchased: completed items with completed_at in last 5 days
  const recentlyPurchased = useMemo(() => {
    const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);
    return items
      .filter((item) => {
        if (!item.isCompleted || !item.completed_at) return false;
        const completedDate = fromTimestamp(item.completed_at);
        return completedDate && completedDate >= fiveDaysAgo;
      })
      .sort((a, b) => {
        const aTime = a.completed_at?.seconds ?? 0;
        const bTime = b.completed_at?.seconds ?? 0;
        return bTime - aTime;
      });
  }, [items]);

  // Buy Again: items purchased 2+ times in 90 days, avg interval >= 5 days
  const buyAgainItems = useMemo(() => {
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    const now = Date.now();

    // Group completed items by normalized title
    const titleGroups: Record<
      string,
      { title: string; completedDates: number[] }
    > = {};

    for (const item of items) {
      if (!item.isCompleted || !item.completed_at) continue;
      const completedDate = fromTimestamp(item.completed_at);
      if (!completedDate || completedDate < ninetyDaysAgo) continue;

      const normalizedTitle = item.title.trim().toLowerCase();
      if (!titleGroups[normalizedTitle]) {
        titleGroups[normalizedTitle] = {
          title: item.title,
          completedDates: [],
        };
      }
      titleGroups[normalizedTitle].completedDates.push(completedDate.getTime());
    }

    const suggestions: {
      title: string;
      lastPurchased: number;
      avgInterval: number;
      overdueRatio: number;
    }[] = [];

    for (const group of Object.values(titleGroups)) {
      if (group.completedDates.length < 2) continue;
      const sorted = group.completedDates.sort((a, b) => a - b);
      const intervals: number[] = [];
      for (let i = 1; i < sorted.length; i++) {
        intervals.push((sorted[i] - sorted[i - 1]) / (1000 * 60 * 60 * 24));
      }
      const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      if (avgInterval < 5) continue;

      const lastPurchased = sorted[sorted.length - 1];
      const daysSinceLast = (now - lastPurchased) / (1000 * 60 * 60 * 24);
      const overdueRatio = daysSinceLast / avgInterval;

      suggestions.push({
        title: group.title,
        lastPurchased,
        avgInterval,
        overdueRatio,
      });
    }

    // Sort by overdue ratio descending (most overdue first)
    return suggestions.sort((a, b) => b.overdueRatio - a.overdueRatio);
  }, [items]);

  function toggleCategory(cat: string) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  }

  async function handleAdd() {
    const title = newTitle.trim();
    if (!title || !householdId || !profile?.uid) return;

    await addDoc(collection(db, `households/${householdId}/groceryListItems`), {
      title,
      category: newCategory,
      created_at: Timestamp.now(),
      created_by: profile.uid,
      household_id: householdId,
      isCompleted: false,
    });
    setNewTitle("");
  }

  async function handleToggle(item: GroceryListItem) {
    if (!item.uid || !householdId) return;
    const ref = doc(db, `households/${householdId}/groceryListItems`, item.uid);
    const newCompleted = !item.isCompleted;
    await updateDoc(ref, {
      isCompleted: newCompleted,
      ...(newCompleted ? { completed_at: Timestamp.now() } : { completed_at: null }),
    });
  }

  async function handleUndoPurchase(item: GroceryListItem) {
    if (!item.uid || !householdId) return;
    const ref = doc(db, `households/${householdId}/groceryListItems`, item.uid);
    await updateDoc(ref, { isCompleted: false, completed_at: null });
  }

  async function handleDelete(item: GroceryListItem) {
    if (!item.uid || !householdId) return;
    const ref = doc(db, `households/${householdId}/groceryListItems`, item.uid);
    await deleteDoc(ref);
  }

  async function handleBuyAgainAdd(title: string) {
    if (!householdId || !profile?.uid) return;
    await addDoc(collection(db, `households/${householdId}/groceryListItems`), {
      title,
      category: "Other",
      created_at: Timestamp.now(),
      created_by: profile.uid,
      household_id: householdId,
      isCompleted: false,
    });
  }

  async function extractPdfText(file: File): Promise<string> {
    const pdfjs = await import("pdfjs-dist");
    const workerUrl = (
      await import("pdfjs-dist/build/pdf.worker.min.mjs?url")
    ).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    const parts: string[] = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const text = content.items
        .map((item) => ("str" in item ? (item.str as string) : ""))
        .join(" ");
      if (text.trim()) parts.push(text);
    }
    return parts.join("\n");
  }

  async function handleScanReceipt(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !householdId) return;
    setScanning(true);
    try {
      const functions = getFunctions();
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      if (isPdf) {
        const receiptText = await extractPdfText(file);
        if (!receiptText.trim()) {
          alert(t("grocery.scan.errors.pdfNoText"));
          return;
        }
        const scan = httpsCallable<
          { householdId: string; receiptText: string },
          { items?: number; itemsAdded?: number; titles?: string[] }
        >(functions, "scanReceipt");
        const result = await scan({ householdId, receiptText });
        const count =
          result.data?.items ??
          result.data?.itemsAdded ??
          result.data?.titles?.length ??
          0;
        setScanBanner({ count });
      } else {
        const reader = new FileReader();
        const imageBase64: string = await new Promise((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.includes(",") ? result.split(",")[1] : result;
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const scan = httpsCallable<
          { householdId: string; imageBase64: string },
          { items?: number; itemsAdded?: number; titles?: string[] }
        >(functions, "scanReceipt");
        const result = await scan({ householdId, imageBase64 });
        const count =
          result.data?.items ??
          result.data?.itemsAdded ??
          result.data?.titles?.length ??
          0;
        setScanBanner({ count });
      }
    } catch (err) {
      console.error("Receipt scan failed:", err);
      alert(t("grocery.scan.errors.scanFailed"));
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleRefreshMeals() {
    if (!householdId) return;
    // Navigate to /meals first so the user sees the dedicated "refreshing"
    // banner there. Fire the CF in parallel; the listener on the Meals
    // page picks up the new docs when they arrive.
    setScanBanner(null);
    navigate("/meals");
    try {
      const functions = getFunctions();
      const regenerate = httpsCallable(functions, "regenerateHouseholdMeals");
      await regenerate({ householdId });
    } catch (err) {
      console.error("Failed to refresh meals:", err);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  const label = household?.household_label ?? t("grocery.householdFallback");

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("grocery.title", { label })}</h1>
        <div className="flex items-center gap-2">
          {receiptScanLocked && <ZoeyPlusLockBadge />}
          <button
            onClick={() => {
              if (receiptScanLocked) {
                openPaywall({
                  title: t("grocery.scan.paywallTitle"),
                  subtitle: t("grocery.scan.paywallSubtitle"),
                });
                return;
              }
              fileInputRef.current?.click();
            }}
            disabled={scanning}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            title={
              receiptScanLocked
                ? t("grocery.scan.tooltipLocked")
                : t("grocery.scan.tooltip")
            }
            aria-label={t("grocery.scan.tooltip")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          className="hidden"
          onChange={handleScanReceipt}
        />
      </div>

      {/* Scanning overlay */}
      {scanning && (
        <Card>
          <div className="flex items-center gap-3 py-2">
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
            />
            <p className="text-sm font-medium">{t("grocery.scan.scanning")}</p>
          </div>
        </Card>
      )}

      {/* Scan success banner */}
      {scanBanner && (
        <Card>
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {t(
                scanBanner.count === 1
                  ? "grocery.scan.banner.one"
                  : "grocery.scan.banner.other",
                { count: scanBanner.count },
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleRefreshMeals}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] transition-opacity hover:opacity-90"
              >
                {t("grocery.scan.refreshMeals")}
              </button>
              <button
                onClick={() => setScanBanner(null)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 dark:bg-white/10 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
              >
                {t("grocery.scan.dismiss")}
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Add item with category */}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAdd();
          }}
          className="space-y-2"
        >
          <div className="flex gap-2">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t("grocery.addItem.placeholder")}
              className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
            <button
              type="submit"
              disabled={!newTitle.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
            >
              {t("common.add")}
            </button>
          </div>
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
          >
            {GroceryCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </form>
      </Card>

      {/* Grouped items */}
      {items.length === 0 ? (
        <EmptyState
          icon="🥬"
          title={t("grocery.empty")}
        />
      ) : (
        <div className="space-y-4">
          {/* Tag cloud — quick-filter chips for visible categories */}
          {sortedCategories.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setActiveCategoryFilter(null)}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeCategoryFilter === null
                    ? "bg-[color:var(--accent-color)] text-white"
                    : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                }`}
              >
                {t("grocery.tagCloud.all")}
              </button>
              {sortedCategories.map((cat) => {
                const active = activeCategoryFilter === cat;
                const count = groupedItems[cat]?.length ?? 0;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategoryFilter(active ? null : cat)}
                    className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color)] text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {cat} <span className="opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {sortedCategories
            .filter((cat) => !activeCategoryFilter || cat === activeCategoryFilter)
            .map((cat) => {
            const catItems = groupedItems[cat];
            const isCollapsed = collapsedCategories.has(cat);
            const completedCount = catItems.filter((i) => i.isCompleted).length;

            return (
              <div key={cat}>
                <button
                  onClick={() => toggleCategory(cat)}
                  className="flex items-center gap-2 w-full px-1 py-2 text-left"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform text-gray-400 ${isCollapsed ? "" : "rotate-90"}`}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  <span className="font-semibold text-sm">{cat}</span>
                  <span className="text-xs text-gray-400">
                    {completedCount}/{catItems.length}
                  </span>
                </button>

                {!isCollapsed && (
                  <div className="space-y-1 ml-1">
                    {catItems.map((item) => {
                      const creator = memberMap[item.created_by];
                      const price = priceMap[item.title.toLowerCase().trim()];
                      return (
                        <Card key={item.uid} padding="p-2">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleToggle(item)}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left"
                            >
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                  item.isCompleted
                                    ? "bg-[color:var(--accent-color)] border-[color:var(--accent-color)]"
                                    : "border-gray-300"
                                }`}
                              >
                                {item.isCompleted && (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className={item.isCompleted ? "line-through text-gray-400 text-sm" : "text-sm"}>
                                  {item.title}
                                </span>
                                {price && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    ~${price.cheapest_price.toFixed(2)} at {price.store ?? price.source ?? "store"}
                                  </p>
                                )}
                                {creator && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <Avatar
                                      src={creator.profile_url}
                                      alt={creator.nickname ?? creator.name ?? ""}
                                      size={14}
                                    />
                                    <span className="text-xs text-gray-400">
                                      {t("grocery.addedBy", { name: creator.nickname ?? creator.name ?? t("grocery.addedBySomeone") })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </button>
                            {!item.isCompleted && isAdult && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSendStoreQuery(item.title);
                                }}
                                className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                                title={`Send "${item.title}" to a store…`}
                                aria-label={`Send "${item.title}" to a store`}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDelete(item);
                              }}
                              className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title={t("grocery.delete.title", { title: item.title })}
                              aria-label={t("grocery.delete.aria", { title: item.title })}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Estimated Total */}
      {(() => {
        const activeItems = items.filter((i) => !i.isCompleted);
        const total = activeItems.reduce((sum, item) => {
          const price = priceMap[item.title.toLowerCase().trim()];
          return sum + (price?.cheapest_price ?? 0);
        }, 0);
        return activeItems.length > 0 && total > 0 ? (
          <p className="text-sm text-green-600 dark:text-green-400 px-1">
            ~${total.toFixed(2)} total
          </p>
        ) : null;
      })()}

      {/* Bulk "Send to {store}" CTA hidden pre-launch — current UX
          decision is per-item action sheets only. Reinstate behind a
          flag once the bulk-send experience is ready. */}

      {/* Recently Purchased */}
      {recentlyPurchased.length > 0 && (
        <div>
          <button
            onClick={() => setShowRecentlyPurchased(!showRecentlyPurchased)}
            className="flex items-center gap-2 w-full px-1 py-2 text-left"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform text-gray-400 ${showRecentlyPurchased ? "rotate-90" : ""}`}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span className="font-semibold text-sm text-gray-500">
              {t("grocery.recentlyPurchased", { count: recentlyPurchased.length })}
            </span>
          </button>

          {showRecentlyPurchased && (
            <div className="space-y-1 ml-1">
              {recentlyPurchased.map((item) => {
                const completedDate = fromTimestamp(item.completed_at);
                return (
                  <Card key={item.uid} padding="p-2">
                    <div className="flex items-center gap-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-500 shrink-0"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <span className="line-through text-gray-400 text-sm">
                          {item.title}
                        </span>
                        {completedDate && (
                          <p className="text-xs text-gray-400">
                            {relativeDate(completedDate)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleUndoPurchase(item)}
                        className="text-xs font-medium shrink-0"
                        style={{ color: "var(--accent-color, #00C7BE)" }}
                      >
                        Undo
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Buy Again */}
      {buyAgainItems.length > 0 && (
        <div>
          <button
            onClick={() => setShowBuyAgain(!showBuyAgain)}
            className="flex items-center gap-2 w-full px-1 py-2 text-left"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform text-gray-400 ${showBuyAgain ? "rotate-90" : ""}`}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span className="font-semibold text-sm text-gray-500">
              {t("grocery.buyAgain", { count: buyAgainItems.length })}
            </span>
          </button>

          {showBuyAgain && (
            <div className="space-y-1 ml-1">
              {buyAgainItems.map((item) => {
                const daysSinceLast = Math.round(
                  (Date.now() - item.lastPurchased) / (1000 * 60 * 60 * 24)
                );
                const avgDays = Math.round(item.avgInterval);
                return (
                  <Card key={item.title} padding="p-2">
                    <div className="flex items-center gap-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-gray-400 shrink-0"
                      >
                        <polyline points="23 4 23 10 17 10" />
                        <polyline points="1 20 1 14 7 14" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{item.title}</span>
                        <div className="flex gap-2 text-xs text-gray-400">
                          <span>{t("grocery.buyAgainLast", { days: daysSinceLast })}</span>
                          <span>{t("grocery.buyAgainAvg", { days: avgDays })}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleBuyAgainAdd(item.title)}
                        className="text-xs font-medium px-2 py-1 rounded-lg shrink-0"
                        style={{
                          color: "var(--accent-color, #00C7BE)",
                          backgroundColor: "var(--accent-color, #00C7BE)15",
                        }}
                      >
                        + {t("common.add")}
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {sendStoreQuery !== null && (
        <SendToStoreSheet
          query={sendStoreQuery}
          preferred={profile?.preferred_store}
          onClose={() => setSendStoreQuery(null)}
        />
      )}
    </div>
  );
}
